(() => {
  if (customElements.get('ag-product-select')) return;

  class AgProductSelect extends HTMLElement {
    connectedCallback() {
      if (this.controller || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
      this.select = this.querySelector('select');
      const template = this.querySelector('[data-ag-select-template]');
      if (!this.select || !template) return;
      this.controller = new AbortController();
      const events = { signal: this.controller.signal };
      this.label = this.select.labels[0];
      this.name = this.label?.textContent.trim() || this.select.name;
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.id = `${this.select.id}-trigger`;
      this.trigger.className = 'ag-product-select__trigger';
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.dialog = template.content.firstElementChild.cloneNode(true);
      this.dialog.id = `${this.select.id}-dialog`;
      this.dialog.setAttribute('aria-label', this.name);
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.dialog.querySelector('.ag-product-select__title').textContent = this.name;
      this.list = this.dialog.querySelector('[role="listbox"]');
      this.list.setAttribute('aria-label', this.name);
      this.buttons = Array.from(this.select.options, (option, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ag-product-select__option';
        button.setAttribute('role', 'option');
        button.disabled = option.disabled;
        const text = document.createElement('span');
        text.textContent = option.text;
        button.append(text);
        const check = this.querySelector('[data-ag-select-check]').content.cloneNode(true);
        check.firstElementChild.setAttribute('aria-hidden', 'true');
        button.append(check);
        button.addEventListener('click', () => this.choose(index), events);
        this.list.append(button);
        return button;
      });
      this.append(this.trigger, this.dialog);
      this.select.hidden = true;
      if (this.label) this.label.htmlFor = this.trigger.id;
      this.sync();
      this.trigger.addEventListener('click', () => this.open(), events);
      this.trigger.addEventListener('keydown', event => {
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault();
          this.open();
        }
      }, events);
      this.select.addEventListener('change', () => this.sync(), events);
      this.dialog.querySelector('.ag-product-select__close').addEventListener('click', () => this.dialog.close(), events);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, events);
      this.dialog.addEventListener('close', () => this.trigger.setAttribute('aria-expanded', 'false'), events);
      this.list.addEventListener('keydown', event => this.navigate(event), events);
      window.addEventListener('resize', () => { if (this.dialog.open) this.position(); }, events);
      window.addEventListener('scroll', event => {
        if (this.dialog.open && !this.dialog.contains(event.target)) this.position();
      }, { ...events, capture: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.searchTimer);
      this.search = '';
      if (this.dialog?.open) this.dialog.close();
      this.dialog?.remove();
      this.trigger?.remove();
      if (this.select) this.select.hidden = false;
      if (this.label) this.label.htmlFor = this.select.id;
    }

    sync() {
      this.trigger.textContent = this.select.options[this.select.selectedIndex]?.text || '';
      this.trigger.setAttribute('aria-label', `${this.name}: ${this.trigger.textContent}`);
      this.trigger.disabled = this.select.disabled;
      this.buttons.forEach((button, index) => {
        button.setAttribute('aria-selected', String(index === this.select.selectedIndex));
        button.tabIndex = index === this.select.selectedIndex ? 0 : -1;
      });
    }

    open() {
      if (this.dialog.open || this.select.disabled) return;
      this.sync();
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.position();
      (this.buttons[this.select.selectedIndex] || this.buttons.find(button => !button.disabled))?.focus({ preventScroll: true });
    }

    position() {
      if (window.matchMedia('(max-width: 767px)').matches) {
        this.dialog.style.removeProperty('left');
        this.dialog.style.removeProperty('top');
        this.dialog.style.removeProperty('width');
        return;
      }
      const box = this.trigger.getBoundingClientRect();
      const width = Math.min(box.width, window.innerWidth - 16);
      this.dialog.style.width = `${width}px`;
      this.dialog.style.left = `${Math.max(8, Math.min(box.left, window.innerWidth - width - 8))}px`;
      const height = this.dialog.getBoundingClientRect().height;
      const below = box.bottom + 4;
      this.dialog.style.top = `${Math.max(8, below + height <= window.innerHeight - 8 ? below : box.top - height - 4)}px`;
    }

    choose(index) {
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.sync();
      this.dialog.close();
      this.trigger.focus({ preventScroll: true });
      if (changed) this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    navigate(event) {
      const enabled = this.buttons.filter(button => !button.disabled);
      const current = enabled.indexOf(document.activeElement);
      let next;
      if (event.key === 'ArrowDown') next = enabled[(current + 1) % enabled.length];
      else if (event.key === 'ArrowUp') next = enabled[(current - 1 + enabled.length) % enabled.length];
      else if (event.key === 'Home') next = enabled[0];
      else if (event.key === 'End') next = enabled[enabled.length - 1];
      else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        clearTimeout(this.searchTimer);
        this.search = (this.search || '') + event.key.toLocaleLowerCase();
        next = enabled.find(button => button.textContent.trim().toLocaleLowerCase().startsWith(this.search));
        this.searchTimer = setTimeout(() => { this.search = ''; }, 700);
      }
      if (!next) return;
      event.preventDefault();
      this.buttons.forEach(button => { button.tabIndex = button === next ? 0 : -1; });
      next.focus();
    }
  }

  customElements.define('ag-product-select', AgProductSelect);
})();
